import React from 'react';
import ReactDOM from 'react-dom/client';
// Archivo (body text face, --fg-body) — self-hosted via @fontsource. Imported
// before the app CSS so the @font-face rules precede any styles that use them.
import '@fontsource/archivo/400.css';
import '@fontsource/archivo/500.css';
import '@fontsource/archivo/600.css';
import '@fontsource/archivo/700.css';
// Global stylesheets MUST import before `App`. `App` pulls in every component's
// co-located stylesheet; if those load before tokens.css the `fg-*` globals
// (`.fg-card`, `.fg-tag`, …) land LATER in the bundle and win equal-specificity
// ties against the component overrides (`.fgd-plan { padding: 0 }` etc.) —
// silently doubling card padding and reverting tag colors app-wide.
// Globals first → component overrides win, as the codebase assumes.
// Production bundles all CSS into one `assets/index.css` (`vite` cssCodeSplit: false)
// so route chunks never inject extra stylesheets alongside the storefront theme mid-page.
import './styles/tokens.css';
import './styles/app.css';
// SPA-page-only `html, body` reset — deliberately NOT part of app.css, which
// the storefront wizard embed shares (see page.css).
import './styles/page.css';
import { App } from './App';
import { readBoot } from './lib/boot';
import { registerStaleChunkAutoReload } from './lib/staleChunkRecovery';
import {
  capturePreviewGrantFromUrl,
  clearStoredPreviewGrant,
  readStoredPreviewGrant,
} from './lib/previewGrant';
import { capturePendingResumeCarryFromUrl } from './lib/anonSession';
import {
  CONVERSION_TRACKING_CONFIG,
  initConversionTracking,
} from './lib/conversionTracking';

registerStaleChunkAutoReload();

// Drop-due resume links land here with `?fg_resume_carry=` (the session
// credential — the app proxy strips the cookie the server also set). Stash it
// as PENDING and scrub the URL before anything renders; AuthProvider decides
// whether to adopt it once the context resolves (anon only).
capturePendingResumeCarryFromUrl();

// Boot data is serialized by the SPA shell into window.__FG_BOOT__ when running
// inside the Shopify app proxy (type + global declared in ./lib/boot). During
// Vite dev the shell is absent and `readBoot` falls back to a default payload.
const boot = readBoot();

// Ad-pixel conversion tracking (Google Ads + Meta) with hardcoded public tag
// ids. Gated on the shell boot being present so Vite dev (no __FG_BOOT__)
// never loads pixels or fires conversions from localhost, and skipped for
// preview-gated loads. Installed pre-render so the bus subscription exists
// before any build can publish, and base pixels load early enough to claim
// gclid/fbclid.
if (window.__FG_BOOT__ && !boot.previewGate) {
  initConversionTracking(CONVERSION_TRACKING_CONFIG);
}

/**
 * Preview-gate refresh rehydration.
 *
 * On a hard refresh the browser issues a plain HTML GET — no `?fg_preview=`, no
 * custom header, and (behind the app proxy) no cookie. The server gates it and
 * embeds `previewGate` in the boot; the React gate path renders straight from
 * boot and never reads sessionStorage. So a valid grant from a prior unlock is
 * stranded and the gate looks like "the code stopped working".
 *
 * Mirror the standalone HTML gate's redirect — but guard against an
 * expired/rotated grant looping: if we arrived gated with the grant ALREADY in
 * the URL (or an explicit `preview_error`), the stored grant is bad, so clear it
 * and show the gate instead of redirecting again. (The server re-gated despite
 * the param → the grant didn't verify.)
 *
 * Returns true when a redirect was triggered, so we skip mounting React for the
 * brief window before navigation takes effect.
 */
function rehydratePreviewGateOrCapture(): boolean {
  if (typeof window === 'undefined') return false;
  if (!boot.previewGate) {
    // Ungated load. If we arrived via the unlock redirect (`?fg_preview=`),
    // stash the grant and clean the URL.
    capturePreviewGrantFromUrl();
    return false;
  }
  try {
    const url = new URL(window.location.href);
    const arrivedWithParam = url.searchParams.has('fg_preview');
    const errored = url.searchParams.get('preview_error') === '1';
    if (arrivedWithParam || errored) {
      // Redirected once and STILL gated (or the server flagged a bad code) →
      // the stored grant is stale. Clear it; render the gate (with code form).
      clearStoredPreviewGrant();
      return false;
    }
    const stored = readStoredPreviewGrant();
    if (!stored) return false;
    url.searchParams.set('fg_preview', stored);
    window.location.replace(url.pathname + url.search);
    return true;
  } catch {
    return false;
  }
}

const redirecting = rehydratePreviewGateOrCapture();

if (!redirecting) {
  const rootEl = document.getElementById('customer-app-root');
  if (!rootEl) {
    throw new Error('customer-app-root element not found');
  }

  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <App boot={boot} />
    </React.StrictMode>,
  );
}
